<div class="admin-system">
  <h1>Administration</h1>

  <div class="tabs">
    <button
      type="button"
      class="tab"
      [class.active]="activeTab === 'organizations'"
      (click)="setTab('organizations')"
    >
      Organizations
    </button>
    <button
      type="button"
      class="tab"
      [class.active]="activeTab === 'groups'"
      (click)="setTab('groups')"
    >
      Org groups
    </button>
    <button
      type="button"
      class="tab"
      [class.active]="activeTab === 'audit'"
      (click)="setTab('audit')"
    >
      Audit trail
    </button>
    <button
      *ngIf="userRoleService.isSupervisorOrSuperAdmin()"
      type="button"
      class="tab"
      [class.active]="activeTab === 'budgets'"
      (click)="setTab('budgets')"
    >
      Budgets
    </button>
  </div>

  <!-- Organizations -->
  <section *ngIf="activeTab === 'organizations'" class="panel">
    <div class="toolbar">
      <button type="button" (click)="showCreateOrgForm = !showCreateOrgForm">
        {{ showCreateOrgForm ? 'Cancel' : 'Create org' }}
      </button>
      <button type="button" (click)="loadOrganizations()" [disabled]="isLoadingOrganizations">
        Refresh
      </button>
    </div>

    <div *ngIf="showCreateOrgForm" class="inline-form">
      <label>
        Name
        <input type="text" name="newOrgName" [(ngModel)]="newOrgName" />
      </label>
      <button type="button" (click)="createOrg(newOrgName)">Save</button>
    </div>

    <p *ngIf="isLoadingOrganizations">Loading…</p>

    <table *ngIf="!isLoadingOrganizations" class="data-table">
      <thead>
        <tr>
          <th>Name</th>
          <th>ID</th>
          <th>Created at</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let org of organizations">
          <td>
            <ng-container *ngIf="editingOrgId !== org.ORGANIZATION_ID; else editName">
              {{ org.ORGANIZATION_NAME }}
            </ng-container>
            <ng-template #editName>
              <input type="text" [(ngModel)]="editOrgName" name="editOrgName-{{ org.ORGANIZATION_ID }}" />
            </ng-template>
          </td>
          <td>{{ org.ORGANIZATION_ID }}</td>
          <td>{{ formatWhen(org.CREATED_AT) }}</td>
          <td class="actions">
            <ng-container *ngIf="editingOrgId !== org.ORGANIZATION_ID; else editActions">
              <button type="button" (click)="startEditOrg(org)">Edit</button>
              <button
                type="button"
                (click)="deleteOrg(org.ORGANIZATION_ID!)"
                [disabled]="org.ORGANIZATION_ID == null"
              >
                Delete
              </button>
            </ng-container>
            <ng-template #editActions>
              <button type="button" (click)="saveEditOrg()">Save</button>
              <button type="button" (click)="cancelEditOrg()">Cancel</button>
            </ng-template>
          </td>
        </tr>
      </tbody>
    </table>
  </section>

  <!-- Org groups -->
  <section *ngIf="activeTab === 'groups'" class="panel">
    <div class="toolbar">
      <button type="button" (click)="showCreateGroupForm = !showCreateGroupForm">
        {{ showCreateGroupForm ? 'Cancel' : 'Create group' }}
      </button>
      <button type="button" (click)="loadOrgGroups()" [disabled]="isLoadingGroups">Refresh</button>
    </div>

    <div *ngIf="showCreateGroupForm" class="inline-form">
      <label>
        Name
        <input type="text" name="newGroupName" [(ngModel)]="newGroupName" />
      </label>
      <label>
        Description
        <input type="text" name="newGroupDescription" [(ngModel)]="newGroupDescription" />
      </label>
      <button type="button" (click)="createGroup(newGroupName, newGroupDescription)">Save</button>
    </div>

    <p *ngIf="isLoadingGroups">Loading…</p>
    <p *ngIf="isLoadingSupervisors">Loading supervisor list…</p>

    <table *ngIf="!isLoadingGroups" class="data-table">
      <thead>
        <tr>
          <th>Name</th>
          <th>Description</th>
          <th>Orgs</th>
          <th>Supervisors</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let g of orgGroups">
          <td>{{ g.GROUP_NAME }}</td>
          <td>{{ g.DESCRIPTION || '—' }}</td>
          <td>{{ orgGroupOrgCount(g) }}</td>
          <td>{{ supervisorSummary(g) }}</td>
          <td>
            <button type="button" (click)="deleteGroup(g.GROUP_ID)">Delete group</button>
          </td>
        </tr>
      </tbody>
    </table>

    <div *ngFor="let g of orgGroups" class="group-card">
      <h3>{{ g.GROUP_NAME }} ({{ g.GROUP_ID }})</h3>
      <p><strong>Members</strong></p>
      <ul>
        <li *ngFor="let m of g.members">
          {{ m.organization?.ORGANIZATION_NAME || m.ORGANIZATION_ID }}
          ({{ m.organization?.ORGANIZATION_ID || m.ORGANIZATION_ID }})
          <button
            type="button"
            *ngIf="m.organization?.ORGANIZATION_ID != null || m.ORGANIZATION_ID != null"
            (click)="
              removeOrgFromGroup(
                g.GROUP_ID,
                (m.organization?.ORGANIZATION_ID ?? m.ORGANIZATION_ID)!
              )
            "
          >
            Remove
          </button>
        </li>
        <li *ngIf="!g.members?.length">No organizations in this group.</li>
      </ul>

      <div class="inline-form">
        <label>
          Add organization
          <select
            name="addOrg-{{ g.GROUP_ID }}"
            [(ngModel)]="selectedOrgIdToAdd[g.GROUP_ID]"
          >
            <option value="">— Select —</option>
            <option *ngFor="let o of orgsAvailableForGroup(g)" [value]="o.ORGANIZATION_ID">
              {{ o.ORGANIZATION_NAME }} ({{ o.ORGANIZATION_ID }})
            </option>
          </select>
        </label>
        <button type="button" (click)="onAddOrgToGroupClick(g)">Add to group</button>
      </div>

      <div class="inline-form">
        <label>
          Assign supervisor
          <select
            name="supervisor-{{ g.GROUP_ID }}"
            [(ngModel)]="selectedSupervisorUserId[g.GROUP_ID]"
            [disabled]="isLoadingSupervisors || !supervisorUsers.length"
          >
            <option value="">— Select user —</option>
            <option *ngFor="let u of supervisorUsers" [value]="u.USER_ID">
              {{ u.FIRST_NAME }} {{ u.LAST_NAME }} ({{ u.USER_ID }}) — {{ u.EMAIL }}
            </option>
          </select>
        </label>
        <button type="button" (click)="onAssignSupervisorClick(g)">Assign</button>
      </div>
    </div>
  </section>

  <!-- Audit -->
  <section *ngIf="activeTab === 'audit'" class="panel">
    <div class="inline-form audit-filters">
      <label>
        From
        <input type="datetime-local" name="auditFrom" [(ngModel)]="auditDateFrom" />
      </label>
      <label>
        To
        <input type="datetime-local" name="auditTo" [(ngModel)]="auditDateTo" />
      </label>
      <button type="button" (click)="applyAuditFilters()" [disabled]="isLoadingAudit">
        Apply
      </button>
      <button type="button" (click)="loadAuditTrail()" [disabled]="isLoadingAudit">Refresh</button>
    </div>

    <p *ngIf="isLoadingAudit">Loading…</p>

    <table *ngIf="!isLoadingAudit" class="data-table">
      <thead>
        <tr>
          <th>Timestamp</th>
          <th>User (ID)</th>
          <th>Action type</th>
          <th>Resource type</th>
          <th>Resource ID</th>
          <th>Details</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let row of auditEntries">
          <td>{{ formatWhen(row.TIMESTAMP) }}</td>
          <td>{{ row.USER_ID }}</td>
          <td>{{ row.ACTION_TYPE }}</td>
          <td>{{ row.RESOURCE_TYPE }}</td>
          <td>{{ row.RESOURCE_ID }}</td>
          <td class="details-cell">{{ formatDetails(row.DETAILS ?? null) }}</td>
        </tr>
      </tbody>
    </table>
  </section>

  <!-- Budgets -->
  <section *ngIf="activeTab === 'budgets'" class="panel">
    <p *ngIf="orgBudgets.length === 0">No organizations loaded.</p>
    <table *ngIf="orgBudgets.length > 0" class="data-table">
      <thead>
        <tr>
          <th>Org name</th>
          <th>Cap (USD)</th>
          <th>Used (USD)</th>
          <th>Remaining (USD)</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let row of orgBudgets; trackBy: trackByOrgId">
          <td>{{ row.orgName }}</td>
          <td>{{ row.snapshot != null ? (row.snapshot.capUsd | currency:'USD') : '—' }}</td>
          <td>{{ row.snapshot != null ? (row.snapshot.usedUsd | currency:'USD') : '—' }}</td>
          <td>{{ row.snapshot != null ? (row.snapshot.remainingUsd | currency:'USD') : '—' }}</td>
          <td>
            <saffron-button text="Edit" (click)="onEditBudgetClick(row.orgId)"></saffron-button>
          </td>
        </tr>
      </tbody>
    </table>

    <app-edit-org-budget-modal
      *ngIf="selectedBudgetOrgId !== null"
      [organizationId]="selectedBudgetOrgId"
      [currentSnapshot]="selectedBudgetSnapshot"
      [open]="editModalOpen"
      (closed)="onBudgetModalClosed()"
      (saved)="onBudgetSaved($event)"
    ></app-edit-org-budget-modal>
  </section>
</div>
